import React, { FC, useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Grid, List, ListItem, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { toast } from 'react-toastify'
import { GreenButton } from '../../../components/Buttons/GreenButton'
import { useAuth, useRest } from '../../../components/Context'
import { InputCodeField } from '../../../components/Dashboard/InputCodeField'
import { Loader } from '../../../components/Loader'
import { resolver } from './resolver'

const useStyles = makeStyles((theme: Theme) => ({
  main: {
    width: '100%',
    minHeight: 'calc(100vh - 248px)',
    padding: '155px 0',
  },
  container: {
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 1184,
    margin: '0 auto',
    paddingTop: 64,
    borderTop: '1px solid rgba(0,0,0,0.24)',
    textAlign: 'center',
  },
  list: {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 24,
    fontWeight: 'bold',
    lineHeight: 2,
    color: theme.palette.primary.dark,
    paddingTop: '0 !important',
    paddingLeft: '48px !important',
    '& li span': {
      color: theme.palette.primary.light,
      fontWeight: 'bold',
      textAlign: 'center',
      lineHeight: 2.1,
      background: theme.palette.info.light,
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      marginRight: '16px',
      fontSize: '16px',
    },
    '& li:nth-child(2)': {
      flexWrap: 'wrap',
    },
  },
  title: {
    padding: '25px 0',
    marginTop: '0 auto',
    fontSize: 40,
    fontWeight: 'bold',
    letterSpacing: 0,
    color: theme.palette.primary.dark,
  },
  qrcode: {
    textAlign: 'center',
    marginBottom: '-30px',
    '& img': {
      width: 240,
    },
  },
  code: {
    width: '280px',
    paddingTop: theme.spacing(2),
    margin: '0 auto',
  },
  error: {
    color: '#E02020',
    fontWeight: 500,
    paddingTop: theme.spacing(2),
  },
  link: {
    color: '#584DE2',
  },
}))

type Inputs = {
  twoFaCode: string
}

export const CreateTwoFactorAuthentication: FC = () => {
  const formMethods = useForm<Inputs>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    resolver,
  })
  const { watch, handleSubmit } = formMethods

  const classes = useStyles()
  const { rest, rxiosAuth2FA } = useRest()
  const { saveToken } = useAuth()

  const [qrCode, setQrCode] = useState<string>()
  const [code, setCode] = useState<string | undefined>('')
  const [isError, setIsError] = useState<boolean>(false)
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    const subscription = watch((value) => {
      setCode(value.twoFaCode)
      if (value?.twoFaCode?.length !== 6) {
        setIsDisabled(true)
        setIsError(false)
      } else {
        setIsDisabled(false)
      }
      return () => subscription.unsubscribe()
    })
  }, [code, watch])

  const blobToBase64 = useCallback((blob: Blob) => {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.readAsDataURL(blob)
      reader.onerror = (error) => {
        toast.error(error)
      }
    })
  }, [])

  const twoFactorGenerateQrCode = useCallback(async () => {
    try {
      const { data } = await rest.post(
        '2fa/generate',
        {},
        {
          responseType: 'blob',
        },
      )
      const Base64 = (await blobToBase64(data)) || ''
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setQrCode(Base64)
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      toast.error(err.message)
    }
  }, [rest, blobToBase64])

  const onSubmit = useCallback(
    async (data: Inputs) => {
      setLoading(true)
      rxiosAuth2FA.post('2fa/turn-on', data).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          const response = { ...next }
          if (response.status === 'success') {
            saveToken(next.access_token)
            setLoading(false)
            formMethods.reset()
          }
        },
        () => {
          setIsError(true)
          setLoading(false)
        },
      )
    },
    [rxiosAuth2FA, formMethods, saveToken],
  )

  const getPressEnterEvent = useCallback((isEnterPressed: boolean, data: Inputs) => {
    if (isEnterPressed) onSubmit(data)
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    twoFactorGenerateQrCode()
  }, [twoFactorGenerateQrCode])

  return (
    <>
      {loading && <Loader />}

      <main className={classes.main}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={4} className={classes.container}>
              <Typography variant="h1" gutterBottom className={classes.title}>
                Set Up Two-Factor Authentication
              </Typography>

              <Grid item xs={6} className={classes.list}>
                <List>
                  <ListItem>
                    <span>1</span>Download Google Authenticator
                  </ListItem>
                  <ListItem>
                    <span>2</span>Use it to scan the QR code
                    <Grid item xs={12} className={classes.qrcode}>
                      <img src={qrCode} alt="Qr Code" />
                    </Grid>
                  </ListItem>
                  <ListItem>
                    <span>3</span>Enter the Authentication Code
                  </ListItem>
                </List>
              </Grid>

              <Grid className={classes.code}>
                <InputCodeField width={40} height={40} isError={isError} getPressEnterEvent={getPressEnterEvent} />
              </Grid>
              {isError && <Typography className={classes.error}>Incorrect two-factor authentication code</Typography>}

              <GreenButton type="submit" width={320} mt={32} text="Verify" disabled={isDisabled || isError} />
            </Grid>
          </form>
        </FormProvider>
      </main>
    </>
  )
}
